import { isAxiosError } from "axios";
import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import { loadCatalog } from "src/business/catalog/apiCatalog";
import { ZCatalog } from "src/business/catalog/ZCatalog";
import { getRecordTitle } from "src/business/records/getRecordTitle";
import { ZCatalogRecordToSave } from "src/business/records/ZCatalogRecord";
import {
  loadWorkflow,
  loadWorkflowCard,
  nextWorkflowStep,
  updateWorkflowCard,
} from "src/business/workflow/apiWorkflow";
import { getWorkflowDisplayName } from "src/business/workflow/getWorkflowDisplayName";
import { getWorkflowStepDisplayName } from "src/business/workflow/getWorkflowStepDisplayName";
import { getNextSteps } from "src/business/workflow/workflowSteps";
import { ZWorkflow } from "src/business/workflow/ZWorkflow";
import { ZWorkflowCard } from "src/business/workflow/ZWorkflowCard";
import { ZWorkflowStep } from "src/business/workflow/ZWorkflowStep";
import { RemoteData } from "src/common/RemoteData";
import { CDSFormStore } from "src/pages/catalogs/CatalogRecordForm/CDSFormStore";
import {
  createWorkflowRecordFormLoader,
  WorkflowRecordFormContext,
} from "src/pages/catalogs/CatalogRecordForm/createWorkflowRecordFormLoader";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

export type WfCardData = {
  card: ZWorkflowCard;
  catalog: ZCatalog;
  workflow: ZWorkflow;
};

const getCurrentWorkflowStep = (
  workflow: ZWorkflow,
  card: ZWorkflowCard,
): ZWorkflowStep => {
  const stepId = card.workflowStep?.id;
  if (!stepId) {
    throw Error("У карточки workflow отсутствует текущий шаг");
  }

  const step = workflow.steps?.find((item) => item.id === stepId);
  if (!step) {
    throw Error("Не найден текущий шаг workflow");
  }

  return step;
};

export const createWfRecordCardStore = () =>
  makeAutoObservable({
    async init(cardId: string) {
      this.setCardId(cardId);
      try {
        this.setData({ status: "wait" });
        const card = await loadWorkflowCard(cardId);
        const { catalogId, workflowId } = card;
        if (!catalogId) {
          throw Error("Отсутствует catalogId");
        }
        if (!workflowId) {
          throw Error("Отсутствует workflowId");
        }
        const [catalog, workflow] = await Promise.all([
          loadCatalog(catalogId),
          loadWorkflow(workflowId, cardId),
        ]);
        this.setData({
          status: "ready",
          result: { card, catalog, workflow },
        });
        const step = getCurrentWorkflowStep(workflow, card);
        this.setCDSFormStore(
          new CDSFormStore(createWorkflowRecordFormLoader(catalog), {
            record: card,
            step,
          }),
        );
      } catch (error) {
        this.setData({ status: "error", error });
      }
    },
    cardId: "",
    setCardId(id: string) {
      this.cardId = id;
    },
    data: { status: "none" } as RemoteData<WfCardData>,
    setData(newData: RemoteData<WfCardData>) {
      this.data = newData;
    },
    cdsFormStore: null as CDSFormStore<WorkflowRecordFormContext> | null,
    setCDSFormStore(store: CDSFormStore<WorkflowRecordFormContext> | null) {
      this.cdsFormStore = store;
    },
    get hasUnsavedChanges(): boolean {
      return Boolean(this.cdsFormStore?.isChanged);
    },
    get availableSteps() {
      const { data } = this;
      if (data.status !== "ready") return [];
      const currentStepId = data.result.card.workflowStep?.id;
      if (!currentStepId) return [];
      return getNextSteps(data.result.workflow.steps ?? [], currentStepId);
    },
    get breadcrumbs(): BreadcrumbItem[] {
      const items: BreadcrumbItem[] = [
        { title: "Рабочие потоки и версии", page: PageUrl.workflowStreams },
      ];
      const { data } = this;
      if (data.status === "ready") {
        items.push({
          title: getWorkflowDisplayName(data.result.workflow),
          page: PageUrl.workflowCards,
          params: { id: data.result.workflow.id },
        });
        items.push({ title: this.title });
      }
      return items;
    },
    get title(): string {
      const { data } = this;
      return data.status === "ready" ? getRecordTitle(data.result.card) : "...";
    },
    get currentStepName(): string | undefined {
      const { data } = this;
      if (data.status !== "ready") return undefined;
      return getWorkflowStepDisplayName(data.result.card.workflowStep);
    },
    get currentStepDescription(): string | null | undefined {
      const { data } = this;
      if (data.status !== "ready") return undefined;
      const stepId = data.result.card.workflowStep?.id;
      if (!stepId) return undefined;
      return data.result.workflow.steps?.find((step) => step.id === stepId)
        ?.description;
    },
    get catalogName(): string | undefined {
      const { data } = this;
      return data.status === "ready"
        ? (data.result.workflow.catalog?.label ?? data.result.catalog.name)
        : undefined;
    },
    async submit(values: ZCatalogRecordToSave) {
      const { data } = this;
      if (data.status !== "ready") throw Error("Данные недоступны");
      const card = await updateWorkflowCard(data.result.card.id, values);
      const { workflow } = data.result;
      const step = getCurrentWorkflowStep(workflow, card);
      this.setData({ status: "ready", result: { ...data.result, card } });
      this.cdsFormStore?.setContext({ record: card, step });
      await this.cdsFormStore?.reload();
      return card;
    },
    async goToNextStep(
      stepId: string,
      navigate: (url: string) => void,
    ): Promise<void> {
      const { data } = this;
      if (data.status !== "ready") throw Error("Данные недоступны");

      const workflowId = data.result.workflow.id;
      const result = await nextWorkflowStep(data.result.card.id, stepId);

      if (result.catalogCardId) {
        navigate(makeUrl(PageUrl.editRecord, { id: result.catalogCardId }));
        return;
      }

      await this.init(this.cardId);

      if (this.data.status !== "error") return;

      if (
        isAxiosError(this.data.error) &&
        this.data.error.response?.status === 403
      ) {
        navigate(makeUrl(PageUrl.workflowCards, { id: workflowId }));
      }
    },
  });

export type WfRecordCardStore = ReturnType<typeof createWfRecordCardStore>;
